import type {
  CreatePackageRequest,
  Package,
  UpdatePackageRequest,
} from "../types/api";
import { getAuthHeaders } from "../utils/auth";

/**
 * Create a new package
 */
export const createPackage = async (
  data: CreatePackageRequest
): Promise<Package> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/packages`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const packageData: Package = await response.json();
    return packageData;
  } catch (error) {
    console.error("Error creating package:", error);
    throw error;
  }
};

/**
 * Get a single package by ID
 */
export const getPackage = async (packageId: string): Promise<Package> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/packages/${packageId}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const packageData: Package = await response.json();
    return packageData;
  } catch (error) {
    console.error("Error fetching package:", error);
    throw error;
  }
};

/**
 * Get all active packages
 */
export const getActivePackages = async (): Promise<Package[]> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/packages?active=true`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const packagesData: Package[] = await response.json();
    return packagesData;
  } catch (error) {
    console.error("Error fetching active packages:", error);
    throw error;
  }
};

/**
 * Get all packages (admin only)
 */
export const getAllPackages = async (): Promise<Package[]> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/packages`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const packagesData: Package[] = await response.json();
    return packagesData;
  } catch (error) {
    console.error("Error fetching all packages:", error);
    throw error;
  }
};

/**
 * Update a package
 */
export const updatePackage = async (
  packageId: string,
  data: UpdatePackageRequest
): Promise<Package> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/packages/${packageId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const packageData: Package = await response.json();
    return packageData;
  } catch (error) {
    console.error("Error updating package:", error);
    throw error;
  }
};

/**
 * Delete a package (soft delete by setting active to false)
 */
export const deletePackage = async (packageId: string): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/packages/${packageId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error("Error deleting package:", error);
    throw error;
  }
};
